import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { CraftsmanService } from '../services/api/craftsmanService';
import { createServiceRequest as createServiceRequestApi } from '../services/api/createServiceRequestApi';
import { Craftsman, ServiceRequest, ServiceCategory } from '../types';
import { CRAFTSMAN_SPECIALTIES } from '../constants/craftsman';

interface ServiceContextType {
  serviceCategories: ServiceCategory[];
  activeRequest: ServiceRequest | null;
  setActiveRequest: (request: ServiceRequest | null) => void;
  nearbyCraftsmen: Craftsman[];
  setNearbyCraftsmen: (craftsmen: Craftsman[]) => void;
  selectedCraftsman: Craftsman | null;
  setSelectedCraftsman: (craftsman: Craftsman | null) => void;
  fetchNearbyCraftsmen: (location: { lat: number; lng: number }) => Promise<void>;
  createServiceRequest: (requestData: Partial<ServiceRequest>) => Promise<ServiceRequest>;
}

const createDefaultServiceCategories = (): ServiceCategory[] =>
  CRAFTSMAN_SPECIALTIES.map((specialty, index) => ({
    id: Number(specialty.id),
    name: specialty.label,
    icon: [
      'Droplet',
      'Lightbulb',
      'Wind',
      'Hammer',
      'Paintbrush',
      'Sparkles',
      'Flower',
      'Camera',
      'Ruler',
      'Droplets',
    ][index] || 'Tool',
    basePrice: [800, 1000, 1200, 600, 1500, 500, 1800, 2000, 1600, 2500][index] || 800,
    estimatedHours: [2, 2, 3, 1, 4, 2, 3, 3, 4, 5][index] || 2,
  }));

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

const fallbackContext: ServiceContextType = {
  serviceCategories: createDefaultServiceCategories(),
  activeRequest: null,
  setActiveRequest: () => {
    if (import.meta.env.DEV) {
      console.warn('setActiveRequest called without ServiceProvider. This is a no-op.');
    }
  },
  nearbyCraftsmen: [],
  setNearbyCraftsmen: () => {
    if (import.meta.env.DEV) {
      console.warn('setNearbyCraftsmen called without ServiceProvider. This is a no-op.');
    }
  },
  selectedCraftsman: null,
  setSelectedCraftsman: () => {
    if (import.meta.env.DEV) {
      console.warn('setSelectedCraftsman called without ServiceProvider. This is a no-op.');
    }
  },
  fetchNearbyCraftsmen: async () => {
    if (import.meta.env.DEV) {
      console.warn('fetchNearbyCraftsmen called without ServiceProvider. Returning empty list.');
    }
  },
  createServiceRequest: async () => {
    throw new Error('ServiceProvider is not initialized. Unable to create service request.');
  },
};

export function ServiceProvider({ children }: { children: ReactNode }) {
  const [activeRequest, setActiveRequest] = useState<ServiceRequest | null>(null);
  const [selectedCraftsman, setSelectedCraftsman] = useState<Craftsman | null>(null);
  const [nearbyCraftsmen, setNearbyCraftsmen] = useState<Craftsman[]>([]);

  const serviceCategories: ServiceCategory[] = createDefaultServiceCategories();

  const fetchNearbyCraftsmen = useCallback(async (location: { lat: number; lng: number }) => {
    try {
      const craftsmen = await CraftsmanService.getNearbyCraftsmen(location);
      setNearbyCraftsmen(craftsmen);
    } catch (error) {
      console.error('Error fetching nearby craftsmen:', error);
    }
  }, []);

  const createServiceRequest = async (requestData: Partial<ServiceRequest>): Promise<ServiceRequest> => {
    const { request, error } = await createServiceRequestApi(requestData);
    if (error || !request) {
      throw new Error(error || '創建服務請求失敗');
    }
    return request;
  };

  return (
    <ServiceContext.Provider
      value={{
        serviceCategories,
        activeRequest,
        setActiveRequest,
        nearbyCraftsmen,
        setNearbyCraftsmen,
        selectedCraftsman,
        setSelectedCraftsman,
        fetchNearbyCraftsmen,
        createServiceRequest,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
}

export function useService() {
  const context = useContext(ServiceContext);
  if (context === undefined) {
    // ✅ 移除警告：因為 Provider 已經正確設置在 main.tsx 中
    // 在 React StrictMode 下，這個警告會出現多次，造成困擾
    // fallbackContext 已經提供了安全的默認值，不會影響功能
    return fallbackContext;
  }
  return context;
}



